import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonInput, IonItem, IonLabel } from "@ionic/react";
import { httpsCallable } from "firebase/functions";
import { useCallback, useEffect, useRef, useState } from "react";
import { functions } from "../../App";

type BackfillResult = {
  scannedStatuses: number;
  affectedGroups: number;
  duplicateGroups: number;
  wouldDeleteCount: number;
  updatedCount: number;
  deletedCount: number;
  skippedMissingKeys: number;
  dryRun?: boolean;
  cutoffUtcMs?: number;
};

const BackfillAssignmentStatuses: React.FC = () => {
  const [running, setRunning] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [result, setResult] = useState<BackfillResult | null>(null);
  const [preview, setPreview] = useState<BackfillResult | null>(null);
  const [previewMessage, setPreviewMessage] = useState("");
  const [previewLoading, setPreviewLoading] = useState(true);
  const [cutoffText, setCutoffText] = useState("");
  const isMountedRef = useRef(true);

  const parseCutoffUtcMs = useCallback(() => {
    const trimmed = cutoffText.trim();
    if (!trimmed) {
      return undefined;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [cutoffText]);

  const callBackfill = useCallback(async (dryRun: boolean) => {
    const callable = httpsCallable(functions, "adminBackfillAssignmentStatuses");
    const cutoffUtcMs = parseCutoffUtcMs();
    const response = await callable({
      dryRun,
      cutoffUtcMs,
    });
    return response.data as BackfillResult;
  }, [parseCutoffUtcMs]);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewMessage("");
    try {
      const payload = await callBackfill(true);
      if (!isMountedRef.current) {
        return;
      }
      setPreview(payload);
      setPreviewMessage("");
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      setPreviewMessage(`Preview failed: ${String(error)}`);
    } finally {
      if (isMountedRef.current) {
        setPreviewLoading(false);
      }
    }
  }, [callBackfill]);

  const runBackfill = async () => {
    if (running) {
      return;
    }
    setRunning(true);
    setResultMessage("");
    setResult(null);
    try {
      const payload = await callBackfill(false);
      setResult(payload);
      setResultMessage("Assignment status backfill completed successfully.");
      await loadPreview();
    } catch (error) {
      setResultMessage(`Backfill failed: ${String(error)}`);
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    loadPreview().catch(() => undefined);
    return () => {
      isMountedRef.current = false;
    };
  }, [loadPreview]);

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Assignment Status Deduplication</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <p>
          Merges duplicate assignment statuses and migrates legacy IDs to the deterministic
          format. Only the 1000 most recently created statuses are scanned.
        </p>
        <IonItem>
          <IonLabel position="stacked">Cutoff timestamp (UTC ms, optional)</IonLabel>
          <IonInput
            value={cutoffText}
            onIonChange={(event) => setCutoffText(event.detail.value || "")}
            placeholder="e.g. 1735689600000"
            inputMode="numeric"
          />
        </IonItem>
        {previewLoading && <p>Loading preview...</p>}
        {preview && (
          <>
            <p><b>Statuses scanned:</b> {preview.scannedStatuses}</p>
            <p><b>Groups to merge or re-ID:</b> {preview.affectedGroups}</p>
            <p><b>Groups with duplicates:</b> {preview.duplicateGroups}</p>
            <p><b>Legacy docs to delete after merge:</b> {preview.wouldDeleteCount}</p>
            <p><b>Skipped (missing assignment/enrollment IDs):</b> {preview.skippedMissingKeys}</p>
          </>
        )}
        {previewMessage && <p>{previewMessage}</p>}
        {result && (
          <>
            <p><b>Groups merged or re-IDed:</b> {result.updatedCount}</p>
            <p><b>Legacy docs deleted:</b> {result.deletedCount}</p>
          </>
        )}
        <IonButton
          expand="block"
          onClick={runBackfill}
          disabled={running}
          aria-label="Run assignment status deduplication backfill"
          title="Run assignment status deduplication backfill"
        >
          {running ? "Running..." : "Run Backfill"}
        </IonButton>
        {resultMessage && <p>{resultMessage}</p>}
      </IonCardContent>
    </IonCard>
  );
};

export default BackfillAssignmentStatuses;
